import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { services } from "@/lib/data";
import { formatDate } from "@/lib/utils";
import { Loader2, Calendar, Clock, CheckCircle2, AlertCircle } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Card, CardContent } from "@/components/ui/card";

// Enhanced schema with required date and time
const bookingFormSchema = z.object({
  name: z.string().min(2, { message: "Name must be at least 2 characters" }),
  email: z.string().email({ message: "Please enter a valid email address" }),
  business: z.string().min(1, { message: "Business name is required" }),
  service: z.string().min(1, { message: "Please select a service" }),
  message: z.string().optional(),
  date: z.string().min(1, { message: "Please select a date" }),
  time: z.string().min(1, { message: "Please select a time slot" }),
});

type BookingFormValues = z.infer<typeof bookingFormSchema>;

interface AvailableSlots {
  date: string;
  available: boolean;
  message: string;
  slots: string[];
}

export default function BookingSection() {
  const { toast } = useToast();
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedDateString, setSelectedDateString] = useState<string | null>(null);
  const [availableTimes, setAvailableTimes] = useState<string[]>([]);
  const [bookingSuccess, setBookingSuccess] = useState<boolean | null>(null);
  const [bookingDetails, setBookingDetails] = useState<BookingFormValues | null>(null);
  
  // Filter available dates (only Tuesday, Wednesday, Thursday and not past dates)
  const filterAvailableDates = (date: Date) => {
    const day = date.getDay();
    // Only allow Tuesday (2), Wednesday (3), and Thursday (4)
    const isAvailableDay = day === 2 || day === 3 || day === 4;
    const isPast = date < new Date(new Date().setHours(0, 0, 0, 0));
    return isAvailableDay && !isPast;
  };
  
  // Fetch available slots for selected date
  const { data: availableSlots, isLoading: isLoadingSlots, error: slotsError } = useQuery<AvailableSlots>({
    queryKey: ['/api/bookings/available-slots', selectedDateString],
    queryFn: () => 
      fetch(`/api/bookings/available-slots?date=${selectedDateString}`)
        .then((res) => {
          if (!res.ok) throw new Error('Failed to fetch available slots');
          return res.json();
        }),
    enabled: !!selectedDateString, // Only run query when date is selected
    refetchOnWindowFocus: false,
  });
  
  // Update available times when slots data changes
  useEffect(() => {
    if (availableSlots?.available && availableSlots.slots.length > 0) {
      setAvailableTimes(availableSlots.slots);
    } else if (availableSlots) {
      setAvailableTimes([]);
    }
  }, [availableSlots]);
  
  // Form handling
  const form = useForm<BookingFormValues>({
    resolver: zodResolver(bookingFormSchema),
    defaultValues: {
      name: "",
      email: "",
      business: "",
      service: "",
      message: "",
      date: "",
      time: "",
    },
  });
  
  // Handle time selection
  const handleTimeSelect = (time: string) => {
    form.setValue("time", time);
    // Clear validation errors for time field
    form.clearErrors("time");
  };
  
  // Submit booking
  const bookingMutation = useMutation({
    mutationFn: (values: BookingFormValues) => {
      return apiRequest("POST", "/api/bookings", values);
    },
    onSuccess: (data) => {
      setBookingSuccess(true);
      setBookingDetails(form.getValues());
      
      // Show success toast
      toast({
        title: "Booking Confirmed",
        description: "We've received your booking request. Check your email for confirmation details.",
        variant: "default",
      });
      
      // Reset form and states
      form.reset();
      setSelectedDate(null);
      setSelectedDateString(null);
      setAvailableTimes([]);
      
      // Invalidate queries to refresh available slots
      queryClient.invalidateQueries({ queryKey: ['/api/bookings/available-slots'] });
    },
    onError: (error: Error) => {
      setBookingSuccess(false);
      
      toast({
        title: "Booking Failed",
        description: error.message || "Something went wrong. Please try again.",
        variant: "destructive",
      });
    },
  });
  
  // Form submission
  function onSubmit(values: BookingFormValues) {
    // Validate that both date and time are selected
    if (!values.date || !values.time) {
      if (!values.date) form.setError("date", { message: "Please select a date" });
      if (!values.time) form.setError("time", { message: "Please select a time slot" });
      return;
    }
    
    bookingMutation.mutate(values);
  }
  
  // Handle reset after successful booking
  const handleResetBooking = () => {
    setBookingSuccess(null);
    setBookingDetails(null);
  };
  
  // If we successfully created a booking, show confirmation
  if (bookingSuccess && bookingDetails) {
    return (
      <section id="booking" className="py-16 md:py-24 relative text-white">
        <div className="absolute inset-0">
          <picture>
            <source srcSet="/images/staff/webp/sarah-calculating.webp" type="image/webp" />
            <source srcSet="/images/staff/sarah-calculating.jpg" type="image/jpeg" />
            <img 
              src="/images/staff/sarah-calculating.jpg" 
              alt="Professional accounting consultation - SW Accounting" 
              className="w-full h-full object-cover"
              loading="lazy"
              width="1200"
              height="800"
            />
          </picture>
          <div className="absolute inset-0 bg-[#1a365d] opacity-90"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-2xl mx-auto bg-white text-[#343a40] p-8 rounded-lg shadow-lg">
            <div className="text-center mb-8">
              <CheckCircle2 className="mx-auto h-16 w-16 text-green-600 mb-4" />
              <h2 className="font-serif text-3xl font-bold text-[#1a365d] mb-2">Booking Confirmed!</h2>
              <div className="w-24 h-1 bg-[#7d9b76] mx-auto mb-4"></div>
              <p className="text-lg">Thank you for scheduling a consultation with SW Accounting.</p>
            </div>
            
            <Card className="mb-6 border-[#e9ecef]">
              <CardContent className="pt-6">
                <div className="space-y-4">
                  <div className="flex items-start">
                    <Calendar className="h-5 w-5 text-[#7d9b76] mr-3 mt-0.5" />
                    <div>
                      <h4 className="font-medium">Date</h4>
                      <p>{formatDate(new Date(bookingDetails.date))}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <Clock className="h-5 w-5 text-[#7d9b76] mr-3 mt-0.5" />
                    <div>
                      <h4 className="font-medium">Time</h4>
                      <p>{bookingDetails.time}</p>
                    </div>
                  </div>
                  
                  <div className="border-t border-[#e9ecef] pt-4 mt-4">
                    <h4 className="font-medium mb-2">Appointment Details</h4>
                    <p><strong>Name:</strong> {bookingDetails.name}</p>
                    <p><strong>Email:</strong> {bookingDetails.email}</p>
                    <p><strong>Business:</strong> {bookingDetails.business}</p>
                    <p><strong>Service:</strong> {bookingDetails.service}</p>
                    {bookingDetails.message && (
                      <p><strong>Additional Information:</strong> {bookingDetails.message}</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Alert className="mb-6 bg-blue-50 border-blue-200">
              <AlertTitle className="text-blue-800">Next Steps</AlertTitle>
              <AlertDescription className="text-blue-700">
                <p className="mb-2">You will receive a confirmation email shortly with all the details.</p>
                <p className="mb-2">Your appointment will be conducted via Zoom video conferencing.</p>
                <p className="font-medium mb-3">A Zoom meeting link and code will be sent to your email before the appointment.</p>
                
                <div className="mt-4 rounded-md overflow-hidden border border-blue-200 bg-blue-50 p-4">
                  <div className="flex items-center justify-center mb-3">
                    <div className="h-16 w-16 bg-blue-100 rounded-full flex items-center justify-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-blue-700" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
                        <line x1="8" y1="21" x2="16" y2="21"></line>
                        <line x1="12" y1="17" x2="12" y2="21"></line>
                      </svg>
                    </div>
                  </div>
                  
                  <div className="text-center">
                    <h4 className="font-medium text-lg text-blue-800 mb-2">Zoom Meeting Details</h4>
                    <p className="mb-3 text-blue-700">Your Zoom meeting code will be emailed to you before your appointment.</p>
                    <p className="mb-4 text-sm text-blue-600">
                      <strong>Consultation Hours:</strong> Tuesday-Thursday, 9:00 AM - 5:00 PM
                    </p>
                  </div>
                </div>
                <div className="mt-2 text-center">
                  <a 
                    href="https://zoom.us/download" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="bg-blue-700 hover:bg-blue-800 text-white px-4 py-2 rounded-md inline-flex items-center transition-colors duration-300"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                      <polyline points="7 10 12 15 17 10"></polyline>
                      <line x1="12" y1="15" x2="12" y2="3"></line>
                    </svg>
                    <span>Download Zoom Client</span>
                  </a>
                </div>
              </AlertDescription>
            </Alert>
            
            <div className="text-center">
              <Button
                onClick={handleResetBooking}
                className="bg-[#1a365d] hover:bg-[#1a365d]/90"
              >
                Book Another Appointment
              </Button>
            </div>
          </div>
        </div>
      </section>
    );
  }
  
  return (
    <section id="booking" className="py-16 md:py-24 relative text-white">
      <div className="absolute inset-0">
        <picture>
          <source srcSet="/images/staff/webp/sarah-calculating.webp" type="image/webp" />
          <source srcSet="/images/staff/sarah-calculating.jpg" type="image/jpeg" />
          <img 
            src="/images/staff/sarah-calculating.jpg" 
            alt="Professional accounting consultation - SW Accounting" 
            className="w-full h-full object-cover"
            loading="lazy"
            width="1200"
            height="800"
          />
        </picture>
        <div className="absolute inset-0 bg-[#1a365d] opacity-90"></div>
      </div>
      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-16">
          <h2 className="font-serif text-3xl md:text-4xl font-bold mb-2">Book a Consultation</h2>
          <div className="w-24 h-1 bg-[#7d9b76] mx-auto mb-4"></div>
          <p className="max-w-2xl mx-auto">Schedule a meeting to discuss how SW Accounting can help your business succeed.</p>
        </div>
        
        <div className="flex flex-col lg:flex-row gap-8">
          <div className="lg:w-1/2 bg-white text-[#343a40] p-8 rounded-lg shadow-lg">
            <h3 className="font-serif text-2xl font-bold text-[#1a365d] mb-6">Schedule a Meeting</h3>
            
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Your Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Full Name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email Address</FormLabel>
                      <FormControl>
                        <Input placeholder="Email Address" type="email" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="business"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Business Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Business Name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="service"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Services Needed</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select a service" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {services.map((service) => (
                            <SelectItem key={service.id} value={service.id}>
                              {service.title}
                            </SelectItem>
                          ))}
                          <SelectItem value="multiple">Multiple Services</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="message"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Additional Information</FormLabel>
                      <FormControl>
                        <Textarea placeholder="Please share any specific details about your needs" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                {/* Hidden date field for validation */}
                <FormField
                  control={form.control}
                  name="date"
                  render={() => (
                    <FormItem className="hidden">
                      <FormControl>
                        <Input type="hidden" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                {/* Hidden time field for validation */}
                <FormField
                  control={form.control}
                  name="time"
                  render={() => (
                    <FormItem className="hidden">
                      <FormControl>
                        <Input type="hidden" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <Button 
                  type="submit" 
                  className="w-full bg-[#7d9b76] hover:bg-opacity-90"
                  disabled={bookingMutation.isPending}
                >
                  {bookingMutation.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Processing...
                    </>
                  ) : "Request Appointment"}
                </Button>
                
                {/* Error display */}
                {form.formState.errors.date || form.formState.errors.time ? (
                  <Alert variant="destructive" className="mt-4">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Error</AlertTitle>
                    <AlertDescription>
                      Please select both a date and time for your appointment.
                    </AlertDescription>
                  </Alert>
                ) : null}
              </form>
            </Form>
          </div>
          
          <div className="lg:w-1/2 bg-white text-[#343a40] p-8 rounded-lg shadow-lg">
            <h3 className="font-serif text-2xl font-bold text-[#1a365d] mb-6">Available Times</h3>
            
            <div className="calendar-widget border border-[#e9ecef] rounded-lg p-6">
              <div className="mb-4">
                <h4 className="font-medium text-lg mb-2">Select a Date</h4>
                <div className="p-3 mb-4 bg-[rgba(26,54,93,0.05)] rounded-md">
                  <p className="text-sm font-medium text-[#1a365d] flex items-center">
                    <Calendar className="h-4 w-4 text-[#7d9b76] mr-2" />
                    Our Draper office is available for appointments on Tuesday, Wednesday, and Thursday only
                  </p>
                </div>
                <div className="datepicker-container">
                  <DatePicker
                    selected={selectedDate}
                    onChange={(date: Date | null) => {
                      if (date) {
                        setSelectedDate(date);
                        const dateStr = date.toISOString().split('T')[0];
                        setSelectedDateString(dateStr);
                        form.setValue("date", dateStr);
                        // Clear any previous time selection
                        form.setValue("time", "");
                        // Clear validation errors for date field
                        form.clearErrors("date");
                      }
                    }}
                    filterDate={filterAvailableDates}
                    minDate={new Date()}
                    inline
                    className="w-full border p-2 rounded"
                  />
                </div>
              </div>
            </div>
            
            {/* Loading state for available slots */}
            {isLoadingSlots && selectedDate && (
              <div className="mt-6 flex items-center justify-center p-4">
                <Loader2 className="h-8 w-8 animate-spin text-[#1a365d]" />
                <span className="ml-2 text-[#1a365d]">Loading available times...</span>
              </div>
            )}
            
            {/* Error state for available slots */}
            {slotsError && selectedDate && (
              <Alert variant="destructive" className="mt-6">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Error</AlertTitle>
                <AlertDescription>
                  Failed to load available time slots. Please try again.
                </AlertDescription>
              </Alert>
            )}
            
            {/* Available time slots display */}
            {selectedDate && availableSlots && !isLoadingSlots && (
              <div className="mt-6">
                <h4 className="font-medium mb-3">
                  Available Time Slots for {formatDate(selectedDate)}
                </h4>
                
                {availableSlots.slots.length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {availableSlots.slots.map((time, index) => (
                      <button 
                        key={index}
                        type="button"
                        className={`border border-[#7d9b76] ${
                          form.getValues("time") === time ? 
                            'bg-[#7d9b76] text-white' : 
                            'text-[#7d9b76] hover:bg-[#7d9b76] hover:text-white'
                        } py-2 rounded-md transition-all duration-300`}
                        onClick={() => handleTimeSelect(time)}
                      >
                        {time}
                      </button>
                    ))}
                  </div>
                ) : (
                  <Alert className="bg-yellow-50 border-yellow-200 text-yellow-800">
                    <AlertCircle className="h-4 w-4 text-yellow-600" />
                    <AlertTitle>No Available Times</AlertTitle>
                    <AlertDescription>
                      All slots for this date have been booked. Please select another date.
                    </AlertDescription>
                  </Alert>
                )}
                
                {/* Calendar integration details */}
                <div className="mt-6 text-center">
                  <p className="text-sm text-gray-600 flex items-center justify-center">
                    <Clock className="h-4 w-4 mr-2 text-[#7d9b76]" />
                    Your appointment will be automatically added to our calendar
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
